import React, { useState, useMemo, useEffect, useRef } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import ComputerIcon from "@mui/icons-material/Computer";
import WarningIcon from "@mui/icons-material/Warning";
import { useGetNocHwQuery } from "api/homeApi";
import { reportError } from "utils/reportError";
import ContentUnavailable from "components/ContentUnavailable";

const PageContainer = styled(Box)({
  backgroundColor: "#FFFFFF",
  minHeight: "100vh",
  padding: "2rem",
});

const PageTitle = styled(Typography)({
  fontFamily: "Montserrat",
  fontWeight: 700,
  fontSize: "1.75rem",
  color: "#1F2937",
  marginBottom: "2rem",
});

const StatsCard = styled(Card)(({ bgcolor }) => ({
  backgroundColor: bgcolor || "#F3F4F6",
  borderRadius: "12px",
  boxShadow: "none",
  border: "1px solid #E5E7EB",
}));

const SystemCard = styled(Card)({
  backgroundColor: "#F9FAFB",
  borderRadius: "16px",
  border: "1px solid #E5E7EB",
  boxShadow: "0 1px 3px rgba(0, 0, 0, 0.1)",
  transition: "box-shadow 0.2s ease-in-out",
  "&:hover": {
    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
  },
});

const InfoRow = styled(Box)({
  display: "flex",
  justifyContent: "space-between",
  padding: "8px 0",
  borderBottom: "1px solid #F3F4F6",
  "&:last-child": {
    borderBottom: "none",
  },
});

const InfoLabel = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: "0.875rem",
  color: "#6B7280",
});

const InfoValue = styled(Typography)({
  fontFamily: "Montserrat",
  fontSize: "0.875rem",
  fontWeight: 500,
  color: "#1F2937",
  textAlign: "right",
  maxWidth: "60%",
  wordBreak: "break-word",
});

const StyledToggleButtonGroup = styled(ToggleButtonGroup)({
  marginBottom: "1rem",
  backgroundColor: "#F3F4F6",
  borderRadius: "12px",
  padding: "4px",
  "& .MuiToggleButton-root": {
    border: "none",
    borderRadius: "8px !important",
    padding: "8px 16px",
    textTransform: "none",
    fontFamily: "Montserrat",
    fontWeight: 500,
    color: "#6B7280",
    "&.Mui-selected": {
      backgroundColor: "#FFFFFF",
      color: "#1F2937",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
      "&:hover": {
        backgroundColor: "#FFFFFF",
      },
    },
    "&:hover": {
      backgroundColor: "rgba(0,0,0,0.04)",
    },
  },
});

const NocHardware = () => {
  const [searchText, setSearchText] = useState("");
  const [filterMode, setFilterMode] = useState("all");

  const { data: responseData, isLoading, isError } = useGetNocHwQuery();
  const errorReported = useRef(false);

  useEffect(() => {
    if (
      !isLoading &&
      !isError &&
      responseData &&
      Array.isArray(responseData.systems) &&
      responseData.systems.length === 0 &&
      !errorReported.current
    ) {
      errorReported.current = true;
      reportError({
        page: "NOC Infrastructure",
        detail: "systems array returned empty from /getNocHw/",
      });
    }
  }, [isLoading, isError, responseData]);

  // Stats
  const stats = useMemo(() => {
    if (!responseData?.systems) return { total: 0, needsUpdate: 0, upToDate: 0 };
    const systems = responseData.systems;
    const needsUpdate = systems.filter((s) => s.needsupdate).length;
    return {
      total: systems.length,
      needsUpdate,
      upToDate: systems.length - needsUpdate,
    };
  }, [responseData]);

  // Filtered systems
  const filteredSystems = useMemo(() => {
    if (!responseData?.systems) return [];

    return responseData.systems.filter((system) => {
      // Filter by status
      if (filterMode === "needs-update" && !system.needsupdate) return false;
      if (filterMode === "up-to-date" && system.needsupdate) return false;

      // Filter by search
      if (searchText) {
        const search = searchText.toLowerCase();
        return (
          system.hostname?.toLowerCase().includes(search) ||
          system.os?.toLowerCase().includes(search) ||
          system.kernel?.toLowerCase().includes(search) ||
          system.make?.toLowerCase().includes(search) ||
          system.model?.toLowerCase().includes(search) ||
          system.serial?.toLowerCase().includes(search)
        );
      }

      return true;
    });
  }, [responseData, filterMode, searchText]);

  if (isLoading) {
    return (
      <PageContainer>
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (isError) {
    return (
      <PageContainer>
        <Alert severity="error">
          Failed to load hardware data. Please try again later.
        </Alert>
      </PageContainer>
    );
  }

  if (!isLoading && responseData?.systems?.length === 0) {
    return (
      <PageContainer>
        <ContentUnavailable page="NOC Infrastructure" />
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <PageTitle>NOC Infrastructure Hardware</PageTitle>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ marginBottom: "2rem" }}>
        <Grid item xs={12} sm={4}>
          <StatsCard bgcolor="#F3F4F6">
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <ComputerIcon sx={{ fontSize: 40, color: "#6B7280", mb: 1 }} />
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#1F2937",
                }}
              >
                {stats.total}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                  color: "#6B7280",
                }}
              >
                Total Machines
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>

        <Grid item xs={12} sm={4}>
          <StatsCard bgcolor="#FEF3C7">
            <CardContent sx={{ textAlign: "center", py: 3 }}>
              <WarningIcon sx={{ fontSize: 40, color: "#D97706", mb: 1 }} />
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "2rem",
                  fontWeight: 700,
                  color: "#92400E",
                }}
              >
                {stats.needsUpdate}
              </Typography>
              <Typography
                sx={{
                  fontFamily: "Montserrat",
                  fontSize: "0.875rem",
                  color: "#92400E",
                }}
              >
                Kernel Update Available
              </Typography>
            </CardContent>
          </StatsCard>
        </Grid>
      </Grid>

      {/* Filters */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        alignItems={{ xs: "stretch", sm: "center" }}
        sx={{ marginBottom: "1.5rem" }}
      >
        <StyledToggleButtonGroup
          value={filterMode}
          exclusive
          onChange={(e, newMode) => {
            if (newMode !== null) setFilterMode(newMode);
          }}
        >
          <ToggleButton value="all">All</ToggleButton>
          <ToggleButton value="needs-update">Update Available</ToggleButton>
          <ToggleButton value="up-to-date">Up to Date</ToggleButton>
        </StyledToggleButtonGroup>

        <TextField
          placeholder="Search systems..."
          size="small"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ color: "#9CA3AF" }} />
              </InputAdornment>
            ),
          }}
          sx={{
            width: { xs: "100%", sm: "300px" },
            backgroundColor: "#F9FAFB",
            borderRadius: "8px",
            "& .MuiOutlinedInput-root": {
              fontFamily: "Montserrat",
              borderRadius: "8px",
            },
          }}
        />
      </Stack>

      {/* Systems Grid */}
      {filteredSystems.length > 0 ? (
        <Grid container spacing={3}>
          {filteredSystems.map((system, index) => (
            <Grid item xs={12} sm={6} lg={4} key={index}>
              <SystemCard>
                <CardContent sx={{ p: 3 }}>
                  {/* Header */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "1rem",
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: "Montserrat",
                        fontSize: "1.125rem",
                        fontWeight: 600,
                        color: "#1F2937",
                      }}
                    >
                      {system.hostname}
                    </Typography>

                    {system.needsupdate ? (
                      <Chip
                        label="Kernel Update Available"
                        size="small"
                        sx={{
                          backgroundColor: "#FEF3C7",
                          color: "#92400E",
                          fontWeight: 600,
                          fontFamily: "Montserrat",
                          fontSize: "0.75rem",
                        }}
                      />
                    ) : (
                      <Chip
                        label="Up to Date"
                        size="small"
                        sx={{
                          backgroundColor: "#D1FAE5",
                          color: "#065F46",
                          fontWeight: 600,
                          fontFamily: "Montserrat",
                          fontSize: "0.75rem",
                        }}
                      />
                    )}
                  </Box>

                  {/* Info */}
                  <Box>
                    <InfoRow>
                      <InfoLabel>OS</InfoLabel>
                      <InfoValue>{system.os || "-"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Kernel</InfoLabel>
                      <InfoValue>{system.kernel || "-"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Make</InfoLabel>
                      <InfoValue>{system.make?.trim() || "-"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Model</InfoLabel>
                      <InfoValue>{system.model?.trim() || "-"}</InfoValue>
                    </InfoRow>
                    <InfoRow>
                      <InfoLabel>Serial</InfoLabel>
                      <InfoValue>{system.serial || "-"}</InfoValue>
                    </InfoRow>
                  </Box>
                </CardContent>
              </SystemCard>
            </Grid>
          ))}
        </Grid>
      ) : (
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography
            sx={{
              fontFamily: "Montserrat",
              fontSize: "1rem",
              color: "#6B7280",
            }}
          >
            No systems found.
          </Typography>
        </Box>
      )}
    </PageContainer>
  );
};

export default NocHardware;
